const normalizeMessageId = (messageId) => String(messageId);
const isOptimisticAssistantId = (messageId) => normalizeMessageId(messageId).startsWith('optimistic-assistant-');
const isTerminalStatus = (status) => status === 'completed' || status === 'failed';

/**
 * Adopts `fetchedMessages` from a messages reload without losing what the
 * socket already delivered. A local assistant message that is still streaming
 * keeps its streamed text while the server reports it non-terminal, and a
 * local terminal message is not regressed by a non-terminal fetch; otherwise
 * the fetched message wins. Local streaming messages the fetch does not yet
 * include are appended, except optimistic placeholders, along with any other
 * local message the fetch lacks for which `keepLocal(message)` is true; both
 * keep their local order. When local ids collide after string normalization,
 * the first local occurrence is used.
 */
const mergeFetchedWithLocalStreamingMessages = (localMessages = [], fetchedMessages = [], keepLocal = () => false) => {
  const fetchedIds = new Set(fetchedMessages.map((messageItem) => normalizeMessageId(messageItem.id)));
  const localMessagesById = new Map();
  localMessages.forEach((messageItem) => {
    const id = normalizeMessageId(messageItem.id);
    if (!localMessagesById.has(id)) localMessagesById.set(id, messageItem);
  });

  const mergedMessages = fetchedMessages.map((fetchedMessage) => {
    const localMessage = localMessagesById.get(normalizeMessageId(fetchedMessage.id));
    if (!localMessage || localMessage.role !== 'assistant') return fetchedMessage;

    if (localMessage.status === 'pending' && !isTerminalStatus(fetchedMessage.status)) {
      return localMessage;
    }

    if (isTerminalStatus(localMessage.status) && !isTerminalStatus(fetchedMessage.status)) {
      return localMessage;
    }

    return fetchedMessage;
  });

  const isLocalStreaming = (localMessage) => (
    localMessage?.role === 'assistant'
    && localMessage?.status === 'pending'
    && !isOptimisticAssistantId(localMessage.id)
  );
  const missingLocalMessages = localMessages.filter((localMessage) => (
    !fetchedIds.has(normalizeMessageId(localMessage?.id))
    && (isLocalStreaming(localMessage) || keepLocal(localMessage))
  ));

  return [...mergedMessages, ...missingLocalMessages];
};

export default mergeFetchedWithLocalStreamingMessages;
